export interface ElectricBikeMetric {
  label: string;
  value: string;
}

export interface ElectricBikeSpec {
  label: string;
  value: string;
}

export interface ElectricBikeColor {
  name: string;
  swatch: string;
  url?: string;
}

export interface ElectricBike {
  id: string;
  name: string;
  fullName: string;
  badge: string;
  tagline: string;
  summary: string;
  price?: string;
  paragraphs: string[];
  focusLabel: string;
  focusValue: string;
  shopUrl: string;
  colors: ElectricBikeColor[];
  metrics: ElectricBikeMetric[];
  highlights: string[];
  specs: ElectricBikeSpec[];
  gallery: string[];
  heroImage: string;
  cardTheme: {
    surface: string;
    border: string;
    tint: string;
    accent: string;
    accentSoft: string;
  };
}

export const electricBikes: ElectricBike[] = [
  {
    id: 'urban-pioneer',
    name: 'Urban Pioneer',
    fullName: 'Bicicleta eléctrica Urban Pioneer',
    badge: 'Confort urbano',
    tagline: 'Comodidad y eficiencia para la ciudad',
    summary:
      'Es una bicicleta eléctrica urbana cómoda y eficiente, con motor de 250W y hasta 100 km de autonomía. Ofrece una conducción estable y segura gracias a su suspensión, frenos hidráulicos y transmisión Shimano de 7 velocidades. Incluye pantalla LED, iluminación completa y un diseño elegante para el uso diario.',
    paragraphs: [
      'La Zwheel Urban Pioneer destaca por su comodidad, accesibilidad y estilo, pensada para una conducción más relajada pero igualmente eficiente.',
      'Incorpora un motor de 250W con 55 Nm de par y una batería de 48V 13Ah, proporcionando una autonomía de hasta 100 km, perfecta para desplazamientos urbanos y recorridos diarios. Su diseño favorece una posición de conducción más cómoda, apoyado por una suspensión delantera de 40 mm, mientras que los neumáticos de 20" x 4.0", la transmisión Shimano de 7 velocidades y los frenos de disco hidráulicos garantizan estabilidad, suavidad y seguridad en la conducción. Equipada con pantalla LED, sistema de iluminación completo e impermeabilidad IP54.',
      'Urban Pioneer combina funcionalidad y diseño en una bicicleta eléctrica pensada para disfrutar cada trayecto con total confort. Disponible en colores beige y verde, ofrece un estilo elegante y diferencial. Tu aliada perfecta para moverte con estilo en el día a día.'
    ],
    focusLabel: 'Pensada para',
    focusValue: 'Trayectos urbanos cómodos y diarios',
    shopUrl: 'https://zwheel-shop.com/product/bicicleta-electrica-urban-pioneer/',
    colors: [
      { name: 'Beige', swatch: '#d6bea0' },
      { name: 'Green grey', swatch: '#7e8976' }
    ],
    metrics: [
      { label: 'Autonomía', value: '65-100 Km' },
      { label: 'Potencia', value: '250W' },
      { label: 'Peso', value: '31,60 Kg' },
      { label: 'Velocidad', value: '25 Km/h' }
    ],
    highlights: [
      'Motor de 250W con 55 Nm de par para una entrega suave y constante.',
      'Batería de 48V 13Ah con autonomía de hasta 100 km para el día a día.',
      'Suspensión delantera de 40 mm que mejora el confort en recorridos urbanos.',
      'Neumáticos 20" x 4.0" y transmisión Shimano de 7 velocidades.',
      'Frenos de disco hidráulicos para una frenada precisa y estable.',
      'Pantalla LED, iluminación completa e impermeabilidad IP54.'
    ],
    specs: [
      { label: 'Potencia', value: '250W' },
      { label: 'Batería', value: '13.000mAh / 48V' },
      { label: 'Autonomía', value: 'Entre 65-100 km' },
      { label: 'Capacidad de carga', value: 'Hasta 120 kg' },
      { label: 'Tipo rueda', value: 'Fat Tire Off Road' },
      { label: 'Tamaño rueda', value: '20" x 4.0"' },
      { label: 'Suspensión', value: 'Delantera de horquilla' },
      { label: 'Freno', value: 'Disco hidráulicos' },
      { label: 'Velocidad máxima', value: '25 km/h' },
      { label: 'Velocidades', value: 'Transmisión Shimano con 7 marchas' },
      { label: 'Peso neto', value: '31,60 kg' },
      { label: 'Peso bruto', value: '38,60 kg' },
      { label: 'Material chasis', value: 'Aluminio' },
      { label: 'Sistema de iluminación', value: 'Faro delantero y luz de freno LED' },
      { label: 'Plegable', value: 'No' },
      { label: 'Dimensiones del vehículo', value: '1780 x 660 x 1150 mm' }
    ],
    gallery: [
      '/images/bicicleta/Urban-Pioneer/85.png',
      '/images/bicicleta/Urban-Pioneer/84.png',
      '/images/bicicleta/Urban-Pioneer/86.png',
      '/images/bicicleta/Urban-Pioneer/91-2.png',
      '/images/bicicleta/Urban-Pioneer/92.png',
      '/images/bicicleta/Urban-Pioneer/93.png',
      '/images/bicicleta/Urban-Pioneer/94.png',
      '/images/bicicleta/Urban-Pioneer/95.png',
      '/images/bicicleta/Urban-Pioneer/96.png',
      '/images/bicicleta/Urban-Pioneer/97.png',
      '/images/bicicleta/Urban-Pioneer/98.png'
    ],
    heroImage: '/images/bicicleta/Urban-Pioneer/84.png',
    cardTheme: {
      surface: 'bg-[#f7f1e7]',
      border: 'border-[#dcc8a8]',
      tint: 'from-[#fbf7f0] via-white to-[#f1f6f1]',
      accent: 'text-[#5e4d35]',
      accentSoft: 'bg-[#efe4d2]'
    }
  },
  {
    id: 'urban-flux',
    name: 'Urban Flux',
    fullName: 'Bicicleta eléctrica Urban Flux',
    badge: 'Compacta plegable',
    tagline: 'Potencia, versatilidad y diseño compacto',
    summary:
      'Bicicleta eléctrica compacta y versátil, ideal para uso diario y rutas exigentes. Ofrece motor de 250W, hasta 100 km de autonomía, neumáticos anchos, transmisión Shimano de 7 velocidades y frenos hidráulicos. Incluye sistema plegable, pantalla LED, iluminación integrada y protección IP54.',
    paragraphs: [
      'Diseñada para quienes buscan una bicicleta eléctrica con carácter aventurero, Zwheel Urban Flux combina potencia, resistencia y versatilidad en un formato compacto y funcional.',
      'Equipada con un motor de 250W y 55 Nm de par, junto a una batería de 48V 13Ah, ofrece una autonomía de hasta 100 km, ideal tanto para el día a día como para rutas más exigentes. Sus neumáticos fat bike de 20" x 4.0" garantizan una excelente tracción en todo tipo de superficies, mientras que la transmisión Shimano de 7 velocidades y los frenos de disco hidráulicos aseguran un control preciso y seguro en cada trayecto.',
      'Además, incorpora sistema de plegado, facilitando su transporte y almacenamiento, junto con pantalla LED a color, iluminación integrada e índice de protección IP54. Está disponible en tres colores: negro, naranja y verde, adaptándose a distintos estilos. Urban Flux es la opción perfecta para quienes buscan rendimiento y practicidad sin límites.'
    ],
    focusLabel: 'Pensada para',
    focusValue: 'Uso diario, escapadas y transporte plegable',
    shopUrl: 'https://zwheel-shop.com/product/bicicleta-electrica-urban-flux/',
    colors: [
      { name: 'Black', swatch: '#1f1f1f' },
      { name: 'Green', swatch: '#3c6642' },
      { name: 'Orange', swatch: '#da7c32' }
    ],
    metrics: [
      { label: 'Autonomía', value: '65-100 Km' },
      { label: 'Potencia', value: '250W' },
      { label: 'Peso', value: '30,80 Kg' },
      { label: 'Velocidad', value: '25 Km/h' }
    ],
    highlights: [
      'Motor de 250W con 55 Nm para combinar respuesta y eficiencia.',
      'Batería de 48V 13Ah con autonomía de hasta 100 km.',
      'Neumáticos fat bike 20" x 4.0" con gran tracción en múltiples superficies.',
      'Transmisión Shimano de 7 velocidades para adaptar el pedaleo al terreno.',
      'Frenos de disco hidráulicos para un control más preciso y seguro.',
      'Sistema plegable, pantalla LED a color, iluminación integrada e IP54.'
    ],
    specs: [
      { label: 'Potencia', value: '250W' },
      { label: 'Batería', value: '13.000mAh / 48V' },
      { label: 'Autonomía', value: 'Entre 65-100 km' },
      { label: 'Capacidad de carga', value: 'Hasta 120 kg' },
      { label: 'Tipo rueda', value: 'Fat Tire Off Road' },
      { label: 'Tamaño rueda', value: '20" x 4.0"' },
      { label: 'Suspensión', value: 'Delantera de horquilla' },
      { label: 'Freno', value: 'Disco hidráulicos' },
      { label: 'Velocidad máxima', value: '25 km/h' },
      { label: 'Velocidades', value: 'Transmisión Shimano con 7 marchas' },
      { label: 'Peso neto', value: '30,80 kg' },
      { label: 'Peso bruto', value: '37,60 kg' },
      { label: 'Material chasis', value: 'Aluminio' },
      { label: 'Sistema de iluminación', value: 'Faro delantero y luz de freno LED' },
      { label: 'Plegable', value: 'Sí' },
      { label: 'Dimensiones del vehículo', value: '1720 x 720 x 1120 mm' },
      { label: 'Dimensiones plegado', value: '780 x 990 x 565 mm' }
    ],
    gallery: [
      '/images/bicicleta/Urban-Flux/Productos-B2B-2.png',
      '/images/bicicleta/Urban-Flux/100.png',
      '/images/bicicleta/Urban-Flux/101.png',
      '/images/bicicleta/Urban-Flux/103.png',
      '/images/bicicleta/Urban-Flux/112.png',
      '/images/bicicleta/Urban-Flux/113.png',
      '/images/bicicleta/Urban-Flux/114-1.png',
      '/images/bicicleta/Urban-Flux/115-1.png',
      '/images/bicicleta/Urban-Flux/116-1.png',
      '/images/bicicleta/Urban-Flux/117-1.png',
      '/images/bicicleta/Urban-Flux/118-1.png',
      '/images/bicicleta/Urban-Flux/119.png'
    ],
    heroImage: '/images/bicicleta/Urban-Flux/100.png',
    cardTheme: {
      surface: 'bg-[#eef4ef]',
      border: 'border-[#b8cdb9]',
      tint: 'from-[#edf5ef] via-white to-[#f6efe6]',
      accent: 'text-[#264230]',
      accentSoft: 'bg-[#dfe9df]'
    }
  },
  {
    id: 'urban-realm',
    name: 'Urban Realm',
    fullName: 'Bicicleta eléctrica Urban Realm',
    badge: 'Carretera urbana',
    tagline: 'La E-Bike de carretera perfecta',
    summary:
      'Bicicleta eléctrica de carretera cómoda, resistente y preparada para cualquier trayecto. Equipada con motor de 250W, 45 Nm de par y batería de 48V 13.5Ah, ofrece hasta 130 km de autonomía, ruedas de 26" x 2.5", transmisión Shimano de 7 velocidades, frenos hidráulicos, sistema plegable, pantalla LCD, iluminación integrada y protección IP54.',
    price: '1.599,99 €',
    paragraphs: [
      'Pensada para quienes buscan una bicicleta eléctrica cómoda, resistente y preparada para cualquier trayecto, esta e-bike combina rendimiento, estabilidad y tecnología en un diseño robusto y funcional.',
      'Equipada con un motor de 250W y 45 Nm de par, junto a una batería de 48V 13.5Ah, ofrece una autonomía de hasta 130 km, ideal tanto para desplazamientos diarios como para rutas más largas.',
      'Sus ruedas de gran tamaño proporcionan mayor estabilidad y agarre en diferentes tipos de terreno, mientras que la suspensión delantera, la transmisión Shimano de 7 velocidades y los frenos de disco hidráulicos garantizan una conducción suave, segura y precisa en cada recorrido.',
      'Además, incorpora pantalla LCD, iluminación delantera y luz LED de freno, mejorando la visibilidad y la experiencia de uso en cualquier momento del día. Su protección IP54 añade resistencia frente al polvo y salpicaduras, aportando mayor fiabilidad en el uso diario.',
      'Disponible en los colores Ash Blue y Forest Green, se adapta a diferentes estilos y preferencias. Una bicicleta eléctrica diseñada para quienes quieren disfrutar de comodidad, autonomía y seguridad en cada aventura.'
    ],
    focusLabel: 'Pensada para',
    focusValue: 'Trayectos diarios, rutas largas y uso urbano estable',
    shopUrl: 'https://zwheel-shop.com/product/bicicleta-electrica-urban-realm/',
    colors: [
      {
        name: 'Ash Blue',
        swatch: '#a8cbd4',
        url: 'https://zwheel-shop.com/escoge-tu-color/ash-blue/'
      },
      {
        name: 'Forest Green',
        swatch: '#244f3f',
        url: 'https://zwheel-shop.com/escoge-tu-color/forest-green/'
      }
    ],
    metrics: [
      { label: 'Autonomía', value: '85-130 Km' },
      { label: 'Potencia', value: '250W' },
      { label: 'Peso', value: '29,50 Kg' },
      { label: 'Velocidad', value: '25 Km/h' }
    ],
    highlights: [
      'Motor de 250W con 45 Nm de par para una asistencia equilibrada.',
      'Batería de 48V 13.5Ah con autonomía de hasta 130 km.',
      'Ruedas de carretera 26" x 2.5" para estabilidad y agarre diario.',
      'Suspensión delantera, transmisión Shimano de 7 velocidades y frenos hidráulicos.',
      'Sistema plegable para facilitar transporte y almacenamiento.',
      'Pantalla LCD, iluminación delantera, luz LED de freno y protección IP54.'
    ],
    specs: [
      { label: 'Potencia', value: '250W' },
      { label: 'Par motor', value: '45 Nm' },
      { label: 'Batería', value: '13.500mAh / 48V' },
      { label: 'Autonomía', value: 'Entre 85-130 km' },
      { label: 'Tipo rueda', value: 'Carretera' },
      { label: 'Tamaño rueda', value: '26" x 2.5"' },
      { label: 'Suspensión', value: 'Delantera de horquilla' },
      { label: 'Freno', value: 'Disco hidráulicos' },
      { label: 'Velocidad máxima', value: '25 km/h' },
      { label: 'Velocidades', value: 'Transmisión Shimano con 7 marchas' },
      { label: 'Peso neto', value: '29,50 kg' },
      { label: 'Pantalla', value: 'LCD' },
      { label: 'Sistema de iluminación', value: 'Faro delantero y luz de freno LED' },
      { label: 'Protección', value: 'IP54' },
      { label: 'Plegable', value: 'Sí' }
    ],
    gallery: [
      '/images/bicicleta/Urban-Realm/Ash-Blue-1.png',
      '/images/bicicleta/Urban-Realm/Ash-Blue-2.png',
      '/images/bicicleta/Urban-Realm/Ash-Blue-3.png',
      '/images/bicicleta/Urban-Realm/Ash-Blue-4.png',
      '/images/bicicleta/Urban-Realm/Ash-Blue-5.png',
      '/images/bicicleta/Urban-Realm/Forest-Green-1.png',
      '/images/bicicleta/Urban-Realm/Forest-Green-2.png',
      '/images/bicicleta/Urban-Realm/Forest-Green-3.png',
      '/images/bicicleta/Urban-Realm/Forest-Green-4.png',
      '/images/bicicleta/Urban-Realm/Forest-Green-5.png'
    ],
    heroImage: '/images/bicicleta/Urban-Realm/Ash-Blue-2.png',
    cardTheme: {
      surface: 'bg-[#eef6f6]',
      border: 'border-[#bdd4d6]',
      tint: 'from-[#edf7f8] via-white to-[#edf5ef]',
      accent: 'text-[#31565e]',
      accentSoft: 'bg-[#dcebec]'
    }
  }
];
